import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import removeMd from "remove-markdown";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import CodeEditor from "components/CodeEditor";
import CodeEditorLoadingSkeleton from "components/CodeEditor/LoadingSkeleton";
import NoAccessPage from "components/error/NoAccessPage";
import FlashContext from "components/FlashMessages/FlashContext";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Select from "ds/components/Select";
import Typography from "ds/components/Typography";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { PolicyEngineType, PolicyTemplate } from "types/generated";
import { AccountContext } from "views/AccountWrapper";

import { createPolicyTypesData } from "../Policies/helpers";
import { FILTERS_ORDER_SETTINGS_KEY } from "../Policies/List/constants";
import { FILTERS_ORDER_SETTINGS_KEY as TEMPLATES_FILTERS_ORDER_SETTINGS_KEY } from "../Policies/Templates/constants";
import { showPolicyCreateDrawer } from "../PolicyCreateDrawer";
import { SpacesContext } from "../SpacesProvider";
import { CREATE_POLICY, GET_TEMPLATE_BODY } from "./gql";
import styles from "./styles.module.css";
import { useDetailsFromUrl } from "./useDetailsFromUrl";

const NewPolicy = () => {
  const navigate = useNavigate();
  const { accountName, viewer } = useTypedContext(AccountContext);
  const { hasManageableSpaces } = useTypedContext(SpacesContext);
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const { name, labels, type, space, description, templateId, takenPolicyNames } =
    useDetailsFromUrl();

  useTitle(`New policy · ${accountName}`);

  const [engineType, setPolicyEngineType] = useState<PolicyEngineType>(PolicyEngineType.RegoV1);

  const [body, setBody] = useState(
    (type && createPolicyTypesData(engineType)[type]?.template) ?? ""
  );

  const handleEditorChange = (value?: string) => {
    setBody(value || "");
  };

  const { loading: templateLoading, data } = useQuery<{ policyTemplate: PolicyTemplate }>(
    GET_TEMPLATE_BODY,
    {
      variables: {
        id: templateId,
      },
      skip: !templateId,
    }
  );

  useEffect(() => {
    if (data?.policyTemplate?.body) {
      setBody(data.policyTemplate.body);
    }
  }, [data?.policyTemplate?.body]);

  // eslint-disable-next-line @cspell/spellchecker
  const [createPolicy, { loading }] = useMutation<{ policyCreatev2: { name: string } }>(
    CREATE_POLICY,
    {
      onCompleted: (data) => {
        if (data) {
          // eslint-disable-next-line @cspell/spellchecker
          reportSuccess({ message: `Policy ${data.policyCreatev2.name} successfully created` });
          navigate(`/policies`);
        }
      },
    }
  );

  const handleCreatePolicy = () => {
    createPolicy({
      variables: {
        input: {
          name,
          body,
          type,
          labels,
          space,
          description,
          policyTemplateULID: templateId,
          engineType,
        },
      },
    }).catch(onError);
  };

  useBreadcrumbs([
    {
      title: "Policies",
      link: templateId
        ? getBreadcrumbsBackUrl("/policies-templates", TEMPLATES_FILTERS_ORDER_SETTINGS_KEY)
        : getBreadcrumbsBackUrl("/policies", FILTERS_ORDER_SETTINGS_KEY),
    },
    {
      title: "New policy",
    },
  ]);

  const subtitle = useMemo(() => description && removeMd(description), [description]);

  const openNewPolicyEditDetailsDrawer = useCallback(() => {
    showPolicyCreateDrawer({
      title: "Edit details",
      submitButtonTitle: "Update",
      takenPolicyNames: takenPolicyNames,
      templateId: templateId,
      defaultValues: {
        name: name || "",
        labels: labels?.map((value) => ({ value })),
        description,
        ...(type && { type }),
        ...(space && { space }),
      },
    });
  }, [description, labels, name, space, takenPolicyNames, templateId, type]);

  if (!viewer.admin && !hasManageableSpaces) {
    return <NoAccessPage />;
  }

  return (
    <>
      <ViewHeader backConfirmation="Your changes will not be saved.">
        <ViewHeaderWrapper direction="row" align="center" justify="between" fullWidth>
          <ViewHeaderTitle subtitle={subtitle}>{name}</ViewHeaderTitle>
          <DropdownMenuEllipsis tooltip="Policy actions" dotsSize="medium">
            <DropdownMenuItem onAction={openNewPolicyEditDetailsDrawer}>
              Edit details
            </DropdownMenuItem>
          </DropdownMenuEllipsis>
        </ViewHeaderWrapper>
      </ViewHeader>

      <Box justify="stretch">
        <Box direction="column" className={styles.bodyWrapper}>
          <Box justify="between" align="center" className={styles.header}>
            <Typography tag="h2" variant="p-t5">
              Policy body
            </Typography>
            <Box gap="medium">
              <Select
                value={engineType}
                items={[
                  { label: "Rego V0", value: PolicyEngineType.RegoV0 },
                  { label: "Rego V1", value: PolicyEngineType.RegoV1 },
                ]}
                onChange={(value: PolicyEngineType) => setPolicyEngineType(value)}
              />
              <Button
                variant="primary"
                disabled={templateLoading}
                loading={loading}
                onPress={handleCreatePolicy}
              >
                Create policy
              </Button>
            </Box>
          </Box>

          {templateLoading ? (
            <CodeEditorLoadingSkeleton />
          ) : (
            <CodeEditor body={body} onChange={handleEditorChange} language="rego" />
          )}
        </Box>
      </Box>
    </>
  );
};

export default NewPolicy;
